import {formatDate} from "@angular/common";

export enum AutomationType {
    BTP_APPLICATION = 'btpServiceRegistration',
    SUITE_REGISTRATION = 'suiteRegistration',
}

export enum OperationalMode {
    CUSTOMER = 'customer',
    SAP = 'sap',
}

export enum BusinessType {
    PRODUCTION = 'production',
    TEST = 'test',
    DEVELOPMENT = 'development',
    OTHER = 'other'
}


export enum IsBTPApplication {
    YES = 'yes',
    NO = 'no',
}

export enum IsReusingSPC {
    YES = 'yes',
    NO = 'no',
}

export enum OperationalScenario {
    UCA_LEASED = 'uca_leased',
    UCA_SPFI = 'uca_spfi',
    EUPORIE = 'euporie',
    SPC_SPFI = 'spc_spfi',
    OWN = 'own',
    CUSTOM = 'custom'
}

export enum Landscape {
    Canary = "canary",
    Live = "prod",
    Staging = "staging",
}

export enum ServiceMetadataCategory {
    AI = "Artificial Intelligence",
    AutonomousEnterprise = "Autonomous Enterprise",
    CloudERPApps = "Cloud ERP Applications",
    DataCloud = "Data and Analytics",
    Transformation = "Transformation",
    Services = "Services",
    Other = "Others"
}

const LandscapesKeyToDisplayName: { [key: string]: string } = {
    [Landscape.Canary]: "Canary",
    [Landscape.Live]: "Live",
    [Landscape.Staging]: "Staging",
};

export {LandscapesKeyToDisplayName};

export const pdfDateFormat = 'dd MMMM yyyy HH:mm';
export const filenameDateFormat = 'yyyy-MM-dd_HH-mm-ss';


export class WizardUtils {
    static getFulfillmentDataYaml(testResourceGroupPath: string, group: string, version: string, type: string, email: string, fullName: string,
        dataCenter = "<Enter a data center. Example: cf-eu12>") {

        const data = {
            apiVersion: "provisioning.api.sap/v2",
            type: "FulfillmentData",
            metadata: {
                name: "level-0-tenant-1",
                path: testResourceGroupPath,
                mixedInto: {
                    apiVersion: `${group}/${version}`,
                    type: type
                }
            },
            spec: {
                customer: {
                    contact: {
                        email: email,
                        name: fullName,
                        phone: "+91 8043291234"
                    },
                    id: "",
                    name: "Some Customer"
                },
                initialUsers: [
                    {
                        email: email,
                        name: fullName
                    }
                ],
                parameters: {
                    dataCenter: dataCenter,
                    businessType: "ZH426",
                    iasUrl: "<Optional. Enter the IAS URL used for setting up trust in the subaccount. Example: zxasdfn.accounts400.ondemand.com>"
                },
                systemRole: "MY_SYSTEM_ROLE",
                systemRoleDisplayName: "My System Role",
                tenantId: Date.now().toString()
            }
        };
        return data;
    }

    static generateParametersReportDefinition(serviceInstanceParametersReport: any, automationSolutionName: string, automationSolutionPath: string) {
        const formattedDate = formatDate(new Date(), pdfDateFormat, 'en-US');
        let content: any[] = [
            {text: formattedDate, alignment: 'right'},
            ' ',
        ];
        content.push({text: "AutomationSolution: Parameter Issues Report", style: "header"});

        content.push('This report lists the issues that were detected when adding the parameters of service instances and/or subscriptions to the generated AutomationSolution for the consumer test subaccount.');
        content.push(' ');
        content.push('Use the Cloud Automation Designer in the Unified Service Workspace tool to fix these issues in the AutomationSolution.');
        content.push(' ');
        content.push('NOTE: The automatic inclusion of the service instance and subscription parameters into your AutomationSolution depends heavily on the service owner\'s implementation so it may be inaccurate. If you have any problems with the report, please contact the SAP owner of the service.');
        content.push(' ');

        if (automationSolutionName && automationSolutionPath) {
            content.push({text: `AutomationSolution: ${automationSolutionName}`, bold: true});
            content.push({text: `Path: ${automationSolutionPath}`, bold: true, margin: [ 0, 2, 0, 0 ]});
            content.push(' ');
        }

        const parametersReportGroupedByType = this.groupedByServiceType(serviceInstanceParametersReport);

        if (parametersReportGroupedByType["service"]) {
            parametersReportGroupedByType["service"].forEach((serviceInstance: any) => {
                content.push({text: `Service: ${serviceInstance.service_name}`, style: "resourceName"});
                content.push({text: `Service Plan: ${serviceInstance.plan_name} | Instance: ${serviceInstance.service_instance_name}`, margin: [ 0, 2, 0, 0 ]});
                content.push(' ');

                const merged = [
                    ...(serviceInstance.create_parameters_validation || []).map((item: any) => ({ ...item, type: 'Create' })),
                    ...(serviceInstance.update_parameters_validation || []).map((item: any) => ({ ...item, type: 'Update' }))
                ];

                this.drawTableParametersReport(merged, content);
            });
        }

        if (parametersReportGroupedByType["subscription"]) {
            parametersReportGroupedByType["subscription"].forEach((subscription: any) => {
                content.push({text: `App Name: ${subscription.service_name}`, style: "resourceName"});
                content.push({text: `App Plan: ${subscription.plan_name}`, margin: [0, 2, 0, 0]});
                content.push(' ');

                const merged = [
                    ...(subscription.create_parameters_validation || []).map((item: any) => ({...item, type: 'Create'})),
                    ...(subscription.update_parameters_validation || []).map((item: any) => ({...item, type: 'Update'}))
                ];

                this.drawTableParametersReport(merged, content);
            });
        }

        let docDefinition: any = {
            content: content,
            styles: {
                header: {
                    fontSize: 18,
                    bold: true,
                    margin: [0, 0, 0, 10]
                },
                link: {
                    color: "blue"
                },
                italic: {
                    italics: true,
                },
                resourceName: {
                    bold: true,
                    decoration: "underline"
                },
                bold: {
                    bold: true
                },
                resourcePath: {
                    bold: true,
                }
            }
        };

        return docDefinition;
    }


    static groupedByServiceType(serviceInstanceParametersReport: any) {
        return serviceInstanceParametersReport.reduce((parametersReport: any, item: any) => {
            const {service_type} = item;
            if (!parametersReport[service_type]) {
                parametersReport[service_type] = [];
            }
            parametersReport[service_type].push(item);
            return parametersReport;
        }, {});
    }

    static drawTableParametersReport(merged: any[], content: any[]) {
        merged.sort((a, b) => this.sortParametersReport(a, b));

        if (merged.length > 0) {
            content.push({
                table: {
                    dontBreakRows: true,
                    headerRows: 1,
                    widths: ['auto', 'auto', 'auto', '*'],

                    body: [
                        [
                            {text: "JSON Path", bold: true},
                            {text: "Parameter Validation", bold: true},
                            {text: "Level", bold: true},
                            {text: "Message", bold: true}
                        ],
                        ...merged.map((param: any) => {
                            return [param.path || "", param.type || "", param.level || "", param.message || ""];
                        }),
                    ]
                }
            });
            content.push(' ');
        }
    }

    static sortParametersReport(a: any, b: any) {
        if (a.path < b.path)  {
            return -1;
        }
        if (a.path > b.path) {
            return 1;
        }
        if (a.type === 'Create' && b.type === 'Update') {
            return -1;
        }
        if (a.type === 'Update' && b.type === 'Create') {
            return 1;
        }
        if (a.level === 'error' && b.level === 'warning') {
            return -1;
        }
        if (a.level === 'warning' && b.level === 'error') {
            return 1;
        }
        return 0;
    }

    static calculateNumberOfParameterIssues(serviceInstanceParametersReport: any[]) {
        let total = 0;
        serviceInstanceParametersReport.forEach((serviceInstance: any) => {
            total += (serviceInstance?.create_parameters_validation || []).length +
                (serviceInstance?.update_parameters_validation || []).length;
        });
        return total;
    }

    static sanitizeToSlug(str: string) {
        return str
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '') // remove special characters
            .trim()
            .replace(/\s+/g, '-');       // replace spaces with dashes
    }

    static cleanString(str: string) {
        return str
            .replace(/[^a-zA-Z0-9\s-]/g, '')
            .replace(/[-\s]+/g, '');
    }

    static toPascalCase(str: string) {
        return str
            .replace(/[^a-zA-Z0-9\s-]/g, '')
            .replace(/[-\s]+/g, ' ')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('');
    }

    static convertToTechnicalName(displayName: string, infix: string) {
        let technicalName = displayName.toLowerCase().replace(/\s+/g, '-');
        if (infix.length) {
            technicalName = `${technicalName}-${infix}`;
        }
        return technicalName;
    }

    static buildDisplayName(displayName: string, stagingInfix: string, stagingParameters: Array<{ displayName: string; infix: string }> = []) {
        const match = stagingParameters.find(param => param.infix === stagingInfix);
        const infix = (stagingInfix.length && match) ? ` ${match.displayName}` : "";
        return `${displayName}${infix}`;
    }

    static convertToNamespace(displayName: string) {
        // Convert to lowercase, remove special chars (keep only letters, digits, spaces), replace spaces with dots
        const namespace = displayName
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, '.');
        return namespace;
    }


    static convertToType(displayName: string) {
        const type = `${displayName.replace(/\s+/g, '')}Tenant`;
        return type.charAt(0).toUpperCase() + type.slice(1);
    }

    static convertToSingular(displayName: string) {
        return `${displayName.toLowerCase().replace(/\s+/g, '')}tenant`;
    }

    static convertToPlural(displayName: string) {
        return `${displayName.toLowerCase().replace(/\s+/g, '')}tenants`;
    }


    static generateGroup(path: string) {
        return path.split("/").filter((p: string) => p).reverse().join(".");
    }
}
